import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {CloseButton} from 'shells/button/close-button/close-button';
import {Link} from 'shells/link/link';
import {ModalHeaderBreadcrumbs} from 'shells/typography/modal-header-breadcrumbs';
import type {ModalHeaderBreadcrumb} from 'shells/typography/modal-header-breadcrumbs';

import type {View} from '../../../../types';

import styles from './add-card-header.module.css';

type Props = {
    title: string;
    currentView: View;
    setCurrentView: (arg1: View) => void;
    onClose: () => void;
    isCreatingField: boolean;
    setNutshellFieldsSearchValue: (arg1: string) => void;
};

export function AddCardHeader(props: Props) {
    const [breadcrumbs, setBreadcrumbs] = React.useState<ModalHeaderBreadcrumb[]>([]);
    const {
        title,
        currentView,
        setCurrentView,
        onClose,
        isCreatingField,
        setNutshellFieldsSearchValue,
    } = props;

    const getTitle = (view: View) => {
        switch (view) {
            case 'short-text':
                return 'Short text fields';
            case 'long-text':
                return 'Long text fields';
            case 'dropdown':
            case 'radio':
                return 'Decision fields';
            case 'checkbox':
                return 'Multiple choice fields';
            case 'date':
                return 'Date fields';
            default:
                return 'Nutshell fields';
        }
    };

    const getCustomFieldType = (view: View): string => {
        let customFieldType = 'custom field';
        switch (view) {
            case 'short-text':
                customFieldType = `short text ${customFieldType}`;
                break;
            case 'long-text':
                customFieldType = `long text ${customFieldType}`;
                break;
            case 'dropdown':
            case 'radio':
                customFieldType = `decision ${customFieldType}`;
                break;
            case 'date':
                customFieldType = `date ${customFieldType}`;
                break;
            case 'checkbox':
                customFieldType = `multiple choice decision ${customFieldType}`;
        }

        return customFieldType;
    };

    React.useEffect(() => {
        const firstBreadcrumb =
            currentView === 'standard'
                ? {text: title}
                : {
                      text: title,
                      onClick: () => {
                          setNutshellFieldsSearchValue('');
                          setCurrentView('standard');
                      },
                  };

        const secondBreadcrumb = isCreatingField
            ? {
                  text: getTitle(currentView),
                  onClick: () => {
                      setCurrentView(currentView);
                  },
              }
            : {
                  text: getTitle(currentView),
              };

        const thirdBreadcrumb = {
            text: 'Create new',
            helpInfo: (
                <>
                    This will create a new <b>{getCustomFieldType(currentView)}</b> in your Nutshell
                    account. You can view or modify these anytime in{' '}
                    <Link
                        // @ts-expect-error
                        as={RoutedLink}
                        to={
                            NutClientConfig.hasPerPipelineCustomFields
                                ? `/setup/custom-field`
                                : '/setup/custom-field-old'
                        }
                        newTab={true}
                        variant='primary'
                        size='small'
                    >
                        settings
                    </Link>
                    .
                </>
            ),
        } as const;

        const newBreadcrumbs = [firstBreadcrumb];

        if (currentView !== 'standard') {
            newBreadcrumbs.push(secondBreadcrumb);
        }

        if (isCreatingField) {
            newBreadcrumbs.push(thirdBreadcrumb);
        }

        setBreadcrumbs(newBreadcrumbs);
    }, [
        currentView,
        isCreatingField,
        setBreadcrumbs,
        setNutshellFieldsSearchValue,
        setCurrentView,
        title,
    ]);

    return (
        <div className={styles.header}>
            <div className={styles.top}>
                <ModalHeaderBreadcrumbs breadcrumbs={breadcrumbs} />
                <CloseButton onClick={onClose} />
            </div>
        </div>
    );
}
